import { useMemo, useRef, useState } from 'react';
import { Check, CircleDot, Loader2, Tag, TriangleAlert, UserRoundPlus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Segmented } from '@/components/ui/segmented';
import { Avatar } from '@/components/ui/avatar-initials';
import { canonicalStatus, cleanText, plural, PROJECT_STATUS_OPTIONS } from './projectsModel';

/*
  One change for several projects (03 #7 bulk bar): Assign Project Engineer · Set status · Add tag.
  `onApply(body)` sends ONE request (POST /projects/bulk-update → one history entry, so the toast's
  Undo puts every project back); an error stays in the dialog.
*/

const META = {
  engineer: { icon: UserRoundPlus, title: 'Assign an engineer', action: 'Assign' },
  status: { icon: CircleDot, title: 'Set status', action: 'Set status' },
  tag: { icon: Tag, title: 'Add a tag', action: 'Add tag' },
};

/**
 * The people as one radio group: a single Tab stop (the picked person, else the first), ↑/↓ (and ←/→,
 * Home/End) move and pick — the WAI-ARIA radio group pattern.
 */
function PersonList({ people, value, onChange }) {
  const [query, setQuery] = useState('');
  const listRef = useRef(null);
  const visible = people.filter((name) => !query || name.toLowerCase().includes(query.trim().toLowerCase()));
  const focusIndex = Math.max(0, visible.indexOf(value));
  const onKeyDown = (event, index) => {
    const moves = { ArrowDown: index + 1, ArrowRight: index + 1, ArrowUp: index - 1, ArrowLeft: index - 1, Home: 0, End: visible.length - 1 };
    if (!(event.key in moves) || !visible.length) return;
    event.preventDefault();
    const next = (moves[event.key] + visible.length) % visible.length;
    listRef.current?.querySelectorAll('[role="radio"]')[next]?.focus();
    onChange(visible[next]);
  };
  return (
    <div className="flex flex-col gap-1.5">
      {people.length > 8 ? (
        <Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Find a person…" aria-label="Find a person" className="h-9" />
      ) : null}
      <div ref={listRef} role="radiogroup" aria-label="Engineer" className="max-h-60 overflow-y-auto rounded-xl border border-line p-1">
        {visible.map((name, index) => {
          const checked = value === name;
          return (
            <button
              key={name}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={index === focusIndex ? 0 : -1}
              onKeyDown={(event) => onKeyDown(event, index)}
              onClick={() => onChange(name)}
              className={cn(
                'flex h-9 w-full items-center gap-2 rounded-lg px-2.5 text-left text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                checked ? 'bg-brand-soft font-semibold text-brand-ink' : 'text-ink hover:bg-surface-hover'
              )}
            >
              <Avatar name={name} size={20} />
              <span className="min-w-0 flex-1 truncate">{name}</span>
              {checked ? <Check className="h-4 w-4" aria-hidden="true" /> : null}
            </button>
          );
        })}
        {!visible.length ? <p className="px-2.5 py-2 text-[12.5px] text-ink-3">No one matches.</p> : null}
      </div>
    </div>
  );
}

export default function BulkEditDialog({ mode, projects = [], people = [], tags = [], onApply, onClose }) {
  const open = Boolean(mode);
  const meta = META[mode] || META.status;
  const [role, setRole] = useState('project_engineer');
  const [person, setPerson] = useState('');
  // No status is picked up front: the user chooses one (the counts show what the projects have now).
  const [status, setStatus] = useState('');
  const [tag, setTag] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const names = useMemo(() => [...new Set(projects.map((p) => cleanText(p.name)).filter(Boolean))], [projects]);
  const statusCounts = useMemo(() => {
    const counts = {};
    projects.forEach((project) => {
      const key = canonicalStatus(project.status);
      counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [projects]);
  const statusNow = PROJECT_STATUS_OPTIONS
    .filter((option) => statusCounts[option.value])
    .map((option) => `${statusCounts[option.value]} ${option.label.toLowerCase()}`)
    .join(' · ');
  const target = names.length === 1 ? `${plural(projects.length, 'project')} of ${names[0]}` : plural(projects.length, 'project');
  const tagSuggestions = tags.filter((item) => cleanText(tag) && item.toLowerCase().includes(cleanText(tag).toLowerCase()) && item.toLowerCase() !== cleanText(tag).toLowerCase()).slice(0, 6);

  const body = mode === 'engineer'
    ? (person ? { [role]: person } : null)
    : mode === 'status'
      ? (status ? { status } : null)
      : (cleanText(tag) ? { add_tags: [cleanText(tag)] } : null);

  const apply = async () => {
    if (!body || saving) return;
    setSaving(true);
    setError('');
    try {
      await onApply(body);
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the change.'));
      setSaving(false);
    }
  };

  const Icon = meta.icon;
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next && !saving) onClose(); }}>
      <DialogContent className="max-w-md gap-4 rounded-card border-line bg-surface text-ink" data-testid="projects-bulk-dialog">
        <DialogHeader className="space-y-1">
          <DialogTitle className="flex items-center gap-2 font-heading text-[17px] font-extrabold">
            <span className="ui-icon-tile tone-navy h-8 w-8" aria-hidden="true"><Icon className="h-4 w-4" /></span>
            {meta.title}
          </DialogTitle>
          <DialogDescription className="text-[13px] text-ink-3">For the {target} you picked.</DialogDescription>
        </DialogHeader>

        {mode === 'engineer' ? (
          <div className="flex flex-col gap-3">
            <Segmented
              ariaLabel="Role"
              value={role}
              onChange={setRole}
              options={[{ key: 'project_engineer', label: 'Project Engineer' }, { key: 'senior_project_engineer', label: 'Senior Project Engineer' }]}
            />
            <PersonList people={people} value={person} onChange={setPerson} />
          </div>
        ) : null}

        {mode === 'status' ? (
          <div className="flex flex-col gap-1.5">
            <Segmented
              ariaLabel="New status"
              value={status}
              onChange={setStatus}
              options={PROJECT_STATUS_OPTIONS.map((option) => ({
                key: option.value,
                label: option.label,
                testId: `projects-bulk-status-${option.value.toLowerCase().replace(/\s+/g, '-')}`,
              }))}
              className="self-start"
            />
            {statusNow ? <p className="text-[12px] text-ink-3" data-testid="projects-bulk-status-now">Now: {statusNow}</p> : null}
          </div>
        ) : null}

        {mode === 'tag' ? (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="projects-bulk-tag" className="text-[12px] font-semibold text-ink-2">Tag</label>
            <Input
              id="projects-bulk-tag"
              value={tag}
              onChange={(event) => setTag(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); apply(); } }}
              placeholder="e.g. Level1"
              className="h-9"
              autoFocus
            />
            {tagSuggestions.length ? (
              <div className="flex flex-wrap gap-1.5">
                {tagSuggestions.map((item) => (
                  <button key={item} type="button" onClick={() => setTag(item)} className="h-7 rounded-full border border-line bg-surface-2 px-2.5 text-[12px] font-semibold text-ink-2 hover:bg-surface-hover">
                    {item}
                  </button>
                ))}
              </div>
            ) : null}
            <p className="text-[12px] text-ink-3">Added to each project; tags they already have stay.</p>
          </div>
        ) : null}

        {error ? (
          <p className="flex items-start gap-1.5 text-[12.5px] font-semibold text-status-red-ink" role="alert">
            <TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {error}
          </p>
        ) : null}

        <DialogFooter className="gap-2">
          <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="button" onClick={apply} disabled={!body || saving} data-testid="projects-bulk-apply">
            {saving ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            {saving ? 'Saving…' : `${meta.action} · ${plural(projects.length, 'project')}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
